<template>
    <div
        class="bg-player-background overflow-hidden rounded-[16px] player-container sticky top-0 left-0 right-0 z-10 p-6 max-[320px]:p-4"
    >
        <div class="flex flex-wrap sm:flex-nowrap items-center">
            <div
                v-if="showCtaCard"
                class="w-full justify-center sm:w-auto sm:pr-8 mb-8 sm:mb-0 shrink-0 hidden sm:flex"
            >
                <CtaCard
                    part="image"
                    :ctas="currentEpisode.ctas"
                    :seek="currentTime"
                    @clicked="onCtaClicked"
                />
            </div>

            <div v-else class="w-full justify-center sm:w-auto sm:pr-8 mb-8 sm:mb-0 shrink-0 hidden sm:flex">
                <img
                    v-if="currentEpisode"
                    :src="art"
                    class="h-[160px] sm:h-[192px] w-[160px] sm:w-[192px] aspect-square rounded-[6px]"
                    alt=""
                />
            </div>
            <div class="w-full min-w-0">
                <div v-if="showCtaCard" class="w-full min-w-0 sm:hidden mb-1">
                    <CtaCard
                        part="mobile"
                        :ctas="currentEpisode.ctas"
                        :seek="currentTime"
                        @clicked="onCtaClicked"
                    />
                </div>

                <div v-if="showCtaCard" class="hidden w-full relative max-w-full sm:block mb-1">
                    <CtaCard
                        part="header"
                        :ctas="currentEpisode.ctas"
                        :seek="currentTime"
                        @clicked="onCtaClicked"
                    />
                </div>

                <div v-if="!showCtaCard" class="w-full relative max-w-full">
                    <div class="font-bold mb-2 w-full min-w-0">
                        <p
                            v-if="playlist[currentTrack]"
                            class="text-player-contrast text-[12px] font-medium mb-3 pr-[100px] break-words"
                            :title="podcast.title"
                            :aria-label="podcast.title"
                        >
                            {{ podcast.title }}
                        </p>
                        <h1
                            v-if="playlist[currentTrack]"
                            :class="[
                                'text-[18px] font-bold text-player-contrast break-words max-sm:truncate',
                                currentEpisode?.ctas?.length ? 'pr-28' : '',
                            ]"
                            :title="playlist[currentTrack].title"
                        >
                            <template v-if="podcast.season">
                                S{{ playlist[currentTrack].season }} / E{{ playlist[currentTrack].episode }}
                            </template>
                            {{ playlist[currentTrack].title }}
                        </h1>
                    </div>

                    <div class="shrink-0 absolute right-0 top-0" v-if="podcast?.branding">
                        <a href="https://www.springcast.io" target="_blank" rel="noopener" class="width: 84px; height: 18px">
                            <img src="/img/springcast_logo.svg" alt="Springcast" width="84" height="18" v-if="logoColor === 'light'" />
                            <img src="/img/springcast_logo_dark.svg" alt="Springcast" width="84" height="18" v-else />
                        </a>
                    </div>

                    <div
                        v-if="!callToActionEnabled && currentEpisode?.ctas?.length"
                        class="pointer-events-none absolute right-0 top-7 z-20 sm:top-8"
                    >
                        <CtaBlock
                            class="pointer-events-auto"
                            :ctas="currentEpisode.ctas"
                            :seek="currentTime"
                            @clicked="onCtaClicked"
                        />
                    </div>
                </div>

                <Timeline 
                    @ended="finalized" 
                    @scroll="scroll" 
                    @timeupdate="saveProgress" 
                    :podcast="podcast" 
                    :track="playlist[currentTrack]"
                    :start-at="startAt"
                    :hide-times="showCtaCard"
                    :compact="showCtaCard"
                    ref="timeline" 
                    @loaded="setProgressIfExists"
                />

                <ShowControls
                    :playing="timeline?.isPlaying"
                    :muted="timeline?.isMuted"
                    :playback-rate="playbackRate"
                    :compact="showCtaCard"
                    :show-share="podcast.share"
                    :seek="timeline?.currentTime ?? 0"
                    :episode-hash-id="playlist[currentTrack]?.hash_id"
                    @play="play"
                    @pause="pause"
                    @mute="mute"
                    @unmute="unmute"
                    @skipSeconds="skip"
                    @backSeconds="back"
                    @playbackRate="handlePlaybackRate"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from "vue";
import Timeline from "./Timeline.vue";
import CtaBlock from "./CtaBlock.vue";
import CtaCard from "./CtaCard.vue";
import { brightnessByColor, resolveActiveCta } from "../helpers.js";
import ShowControls from "./ShowControls.vue";

const props = defineProps({
    podcast: {
        type: Object,
        required: true,
    },
    colors: Object,
    startAt: {
        type: [Number, String, null],
        required: false,
    },
    currentTrack: {
        type: Number,
        required: true,
    },
    playbackRate: {
        type: Number,
        required: true,
    },
});

const emit = defineEmits([
    'finalized',
    'scroll', 
    'saveProgress',
    'setProgressIfExists',
    'play',
    'pause',
    'mute',
    'unmute',
    'skip',
    'back',
    'playbackRate',
    'cta',
]);

const timeline = ref(null);

const currentTime = computed(() => timeline.value?.currentTime ?? 0);

const callToActionEnabled = computed(() => !!props.podcast.call_to_action);

const activeCta = computed(() => {
    if (!callToActionEnabled.value || !currentEpisode.value?.ctas?.length) {
        return null;
    }

    return resolveActiveCta(currentTime.value, currentEpisode.value.ctas);
});

const showCtaCard = computed(() => activeCta.value !== null);

function onCtaClicked(ctaId) {
    emit('cta', ctaId);
}

const playlist = computed(() => Object.values(props.podcast.episodes));
const currentEpisode = computed(() => playlist.value[props.currentTrack]);

const logoColor = computed(() => {
    const brightness = brightnessByColor(props.colors.player_background);

    if (brightness > 100) {
        return 'dark'
    }

    return 'light'
});

const art = computed(() => {
    return currentEpisode.value?.artwork;
});

function finalized() {
    emit('finalized');
}

function scroll() {
    emit('scroll');
}

function saveProgress() {
    emit('saveProgress');
}

function setProgressIfExists() {
    emit('setProgressIfExists');
}

function play() {
    emit('play');
}

function pause() {
    emit('pause');
}

function mute() {
    emit('mute');
}

function unmute() {
    emit('unmute');
}

function skip(seconds) {
    emit('skip', seconds);
}

function back(seconds) {
    emit('back', seconds);
}

function handlePlaybackRate(rate) {
    emit('playbackRate', rate);
}

defineExpose({
    timeline
});
</script>

<style scoped>
/* Disable sticky positioning at high zoom levels (small viewport heights) */
@media (max-height: 600px) {
    .player-container {
        position: static !important;
    }
}
</style> 